import { useEffect, useState } from "react";
import type { Heading } from "../types/content";

export function useReaderState(headings: Heading[]) {
  const [activeHeading, setActiveHeading] = useState(headings[0]?.id ?? "");
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const article = document.querySelector<HTMLElement>("[data-reader-article]");
    if (!article) return;
    const nodes = headings
      .map((heading) => document.getElementById(heading.id))
      .filter((node): node is HTMLElement => Boolean(node));
    let animationFrame = 0;

    const updateReaderState = () => {
      const rect = article.getBoundingClientRect();
      const scrollable = Math.max(article.offsetHeight - window.innerHeight, 1);
      const travelled = Math.min(Math.max(-rect.top + 96, 0), scrollable);
      setProgress(Math.round((travelled / scrollable) * 100));
      const activationLine = Math.min(window.innerHeight * 0.28, 220);
      let nextHeading = nodes[0]?.id ?? "";
      for (const node of nodes) {
        if (node.getBoundingClientRect().top > activationLine) break;
        nextHeading = node.id;
      }
      setActiveHeading(nextHeading);
    };

    const scheduleUpdate = () => {
      if (animationFrame) return;
      animationFrame = window.requestAnimationFrame(() => {
        animationFrame = 0;
        updateReaderState();
      });
    };
    updateReaderState();
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);

    return () => {
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
    };
  }, [headings]);

  return { activeHeading, progress };
}
